import useSWR, { mutate } from 'swr';
import useSWRMutation from 'swr/mutation';
import { SuccessResponseWithMessage } from '@interfaces/central-monitoring';
import { AddSubjectObservationModel } from '@interfaces-clean/subject-monitoring/subject-observations';
import { SubjectReviewNoteDto } from '@interfaces-clean/subject-monitoring.interface';
import { getData, postData } from '@apis/rest-client';

const timeout = 30000;
const reviewNotesUrl = 'SubjectMonitoring/SubjectReviewNotes';

export const SubjectNotesApiKeys = {
    useGetAllNotesQuery: 'GetAllNotesQuery',
    useGetReviewNotesPaginated: 'GetReviewNotesPaginated',
} as const;

export const SubjectNotesApi = {
    invalidateGetAllNotesQuery: (studyRef: string, subjectId: number) => {
        const queryKey = [SubjectNotesApiKeys.useGetAllNotesQuery, studyRef, subjectId];

        // Invalidate queries which match the `queryKey`
        // eslint-disable-next-line no-void
        void mutate((key) => Array.isArray(key) && key.every((ele, i) => ele === queryKey[i]));
    },

    invalidateGetAllNotesForReview: (studyRef: string, reviewId: number) => {
        const queryKey = [SubjectNotesApiKeys.useGetReviewNotesPaginated, studyRef, reviewId];

        // Invalidate queries which match the `queryKey`
        // eslint-disable-next-line no-void
        void mutate((key) => Array.isArray(key) && key.every((ele, i) => ele === queryKey[i]));
    },

    useGetAllNotesQuery: (studyRef: string, subjectId: number) => {
        const key = [SubjectNotesApiKeys.useGetAllNotesQuery, studyRef, subjectId];

        return useSWR(key, () => {
            return getData<SubjectReviewNoteDto[]>(`${reviewNotesUrl}/${studyRef}/${subjectId}`, { timeout });
        });
    },

    useAdd: (studyRef: string, sessionId?: string) => {
        const queryParams = new URLSearchParams();
        if (sessionId !== undefined) {
            queryParams.append('sessionId', sessionId);
        }
        return useSWRMutation(
            `${reviewNotesUrl}/AddNote/${studyRef}?${queryParams}`,
            async (url, { arg }: { arg: AddSubjectObservationModel }) => {
                const result = await postData<AddSubjectObservationModel, SuccessResponseWithMessage>(url, {
                    arg,
                    timeout,
                });

                if (result.success === true) {
                    // Invalidate get-notes queries for studyRef-subjectCode / studyRef-reviewId combo
                    if (arg.subjectId != null) {
                        SubjectNotesApi.invalidateGetAllNotesQuery(studyRef, arg.subjectId);
                    }
                    if (arg.reviewId != null) {
                        SubjectNotesApi.invalidateGetAllNotesForReview(studyRef, arg.reviewId);
                    }
                }

                return result;
            },
        );
    },

    useGetAllNotesForReview: (studyRef: string, reviewId: number) => {
        const key = [SubjectNotesApiKeys.useGetReviewNotesPaginated, studyRef, reviewId];

        return useSWR<SubjectReviewNoteDto[]>(key, () => {
            return getData<SubjectReviewNoteDto[]>(`${reviewNotesUrl}/Review/${studyRef}/${reviewId}`);
        });
    },
} as const;
